import { SendOutlined } from '@ant-design/icons';
import {
  App,
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Drawer,
  Flex,
  Row,
  Tooltip,
  Typography,
} from 'antd';
import { useState } from 'react';
import PropTypes from 'prop-types';
import HarnessInstance from '@/apps/harness/components/HarnessInstance';
import ComposerPanel from '@/common/components/ComposerPanel';
import { useAppContext } from '@/common/hooks/AppStateProvider';
import { useOrganizationPath } from '@/common/hooks/use-organization-path';
import { useQueryParams } from '@/common/hooks/use-query-params';
import { AI_AVATAR } from '@/common/utils/ai-avatar';
import AgentResultPanel from './AgentResultPanel';
import AgentChatThreadList from './AgentChatThreadList';
import AgentEntryPanel from './AgentEntryPanel';
import {
  CREATION_IDEA_MAX_LENGTH,
  createHarnessSourceEntry,
  getCreationIdeaLength,
  getHarnessCreationUrl,
} from '../utils/harness-creation';
import { clearCreationQuery, getCreationRecordQuery } from '../utils/creation-record';
import { useCreationStart } from '../hooks/use-creation-start';

const cardRoot = {
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 128px)',
  maxHeight: 'calc(100vh - 128px)',
};

const AgentCreationWorkspace = ({ creationQuery, entry }) => {
  const { organizationId } = useOrganizationPath();
  const { session } = useAppContext();
  const { message } = App.useApp();
  const [query, setQuery] = useQueryParams();
  const {
    start,
    submitting,
    error: startError,
    retry,
  } = useCreationStart({ organizationId, entry });
  const { threadId } = creationQuery;
  const input = Object.fromEntries(
    Object.entries(creationQuery).filter(([key]) =>
      ['seedPlanId', 'hotPotId', 'idea'].includes(key),
    ),
  );
  const src = threadId ? getHarnessCreationUrl(organizationId, { threadId, ...input }) : undefined;
  const [idea, setIdea] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const ideaLength = getCreationIdeaLength(idea.trim());
  const canSendIdea = Boolean(idea.trim()) && ideaLength <= CREATION_IDEA_MAX_LENGTH;

  const handleSourceStart = async (source) => {
    try {
      if (await start(createHarnessSourceEntry(source).input)) setIdea('');
    } catch (error) {
      message.error(error.message);
    }
  };
  const handleIdeaSubmit = async (event) => {
    event.preventDefault();
    if (!canSendIdea) return;
    if (await start({ idea })) setIdea('');
  };

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col xs={24} lg={12}>
          <Card
            title="创作助手"
            variant="borderless"
            extra={
              <Button type="link" onClick={() => setHistoryOpen(true)}>
                创作记录
              </Button>
            }
            styles={{
              root: cardRoot,
              body: {
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                padding: 0,
              },
            }}
          >
            {src ? (
              <HarnessInstance key={src} fit="container" src={src} />
            ) : (
              <ComposerPanel
                onSubmit={handleIdeaSubmit}
                textAreaProps={{
                  'aria-label': '创作想法',
                  placeholder: '输入你的创作想法，开始创作吧…',
                  value: idea,
                  onChange: (event) => setIdea(event.target.value),
                  autoSize: { minRows: 2, maxRows: 6 },
                  count: {
                    show: true,
                    max: CREATION_IDEA_MAX_LENGTH,
                    strategy: (value) => getCreationIdeaLength(value.trim()),
                  },
                  status: ideaLength > CREATION_IDEA_MAX_LENGTH ? 'error' : undefined,
                  onPressEnter: (event) => {
                    if (!event.shiftKey && !event.nativeEvent.isComposing) handleIdeaSubmit(event);
                  },
                }}
                actions={
                  <Tooltip title="发送">
                    <Button
                      aria-label="发送"
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!canSendIdea}
                      loading={submitting}
                    />
                  </Tooltip>
                }
              >
                {startError && (
                  <Alert
                    type="error"
                    showIcon
                    title="无法开始创作"
                    description={startError.message}
                    action={
                      <Button onClick={retry} loading={submitting}>
                        重试
                      </Button>
                    }
                  />
                )}
                <Flex vertical align="center" gap="small">
                  <Avatar {...AI_AVATAR} size="large" />
                  <Typography.Text>欢迎使用智能体，内容创作从这里开始</Typography.Text>
                </Flex>
                <AgentEntryPanel onEntryStart={handleSourceStart} organizationId={organizationId} />
              </ComposerPanel>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <AgentResultPanel
            key={threadId || 'landing'}
            organizationId={organizationId}
            threadId={threadId}
          />
        </Col>
      </Row>
      <Drawer
        destroyOnHidden
        open={historyOpen}
        title="创作记录"
        size="large"
        closable={{ placement: 'end' }}
        onClose={() => setHistoryOpen(false)}
      >
        <AgentChatThreadList
          currentRole={session.role}
          organizationId={organizationId}
          onThreadSelect={(record) => {
            if (!record?.id) return;
            try {
              setQuery(
                { ...clearCreationQuery(query), ...getCreationRecordQuery(record) },
                { reset: true },
              );
              setHistoryOpen(false);
            } catch (error) {
              message.error(error.message);
            }
          }}
          onThreadRemove={(record) => {
            if (record.id === threadId) setQuery(clearCreationQuery(query), { reset: true });
          }}
        />
      </Drawer>
    </>
  );
};

AgentCreationWorkspace.propTypes = {
  entry: PropTypes.shape({ seedPlanId: PropTypes.string, hotPotId: PropTypes.string }),
  creationQuery: PropTypes.shape({
    threadId: PropTypes.string,
    mode: PropTypes.string,
    seedPlanId: PropTypes.string,
    hotPotId: PropTypes.string,
    idea: PropTypes.string,
  }).isRequired,
};

export default AgentCreationWorkspace;
